import React, { useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";
import { hasActionAccess, hasWorkspaceAccess } from "../../lib/workspaces";

function makeKey(): string {
  const g: any = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "el-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export type EventShape = {
  id: string; title: string; event_type_code: string;
  status: "scheduled" | "pending_verification" | "completed" | "rejected" | "cancelled" | "no_show" | "rescheduled";
  version: number;
  all_day: boolean;
  start_at?: string | null; end_at?: string | null;
  start_date?: string | null; end_date?: string | null;
  location?: string | null; notes?: string | null;
  assigned_to_user_id: string;
  contact_id?: string | null; organization_id?: string | null;
  opportunity_id?: string | null;
  cancelled_reason?: string | null;
  completion_outcome?: string | null;
  completion_evidence_note?: string | null;
  completion_evidence_links?: string[];
  verification_rejection_reason?: string | null;
  submitted_for_verification_at?: string | null;
  completed_at?: string | null;
  verified_at?: string | null;
  verified_by_user_id?: string | null;
  created_at?: string | null; updated_at?: string | null;
  created_by_name?: string | null; assigned_to_name?: string | null;
  updated_by_name?: string | null; completed_by_name?: string | null;
  verified_by_name?: string | null; cancelled_by_name?: string | null;
  no_show_by_name?: string | null; rescheduled_by_name?: string | null;
  cancelled_at?: string | null; no_show_at?: string | null; rescheduled_at?: string | null;
};

type LifecycleMode = "complete" | "cancel" | "no-show" | "reschedule" | "verify" | "reject";

type ItemProps = {
  event: EventShape;
  onRefetch: () => void;
  readOnly?: boolean;
};

export const EventItem: React.FC<ItemProps> = ({ event, onRefetch, readOnly }) => {
  const [dialog, setDialog] = useState<LifecycleMode | null>(null);
  const [reason, setReason] = useState<string>("");
  const [outcome, setOutcome] = useState<string>("");
  const [evidenceNote, setEvidenceNote] = useState<string>("");
  const [evidenceLinks, setEvidenceLinks] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const keyRef = useRef<string>(makeKey());
  const role = useEffectiveRole();
  const { user } = useAuth();
  const canVerify = canAtLeast(role, "manager_trainer") && hasWorkspaceAccess(user, "visit_verification") && hasActionAccess(user, "visit_verification", "edit");
  const badge = useMemo(() => {
    switch (event.status) {
      case "completed": return { text: event.verified_at ? "Verified" : "Completed", cls: "bg-green-50 text-green-700 border-green-200" };
      case "pending_verification": return { text: "Pending verification", cls: "bg-amber-50 text-amber-700 border-amber-200" };
      case "rejected": return { text: "Rejected", cls: "bg-orange-50 text-orange-700 border-orange-200" };
      case "rescheduled": return { text: "Rescheduled", cls: "bg-violet-50 text-violet-700 border-violet-200" };
      case "cancelled": return { text: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" };
      case "no_show": return { text: "No-show", cls: "bg-red-50 text-red-700 border-red-200" };
      default: return { text: "Scheduled", cls: "bg-sky-50 text-sky-700 border-sky-200" };
    }
  }, [event.status]);

  async function runAction(mode: LifecycleMode) {
    setSubmitting(true); setError("");
    try {
      const path = mode === "no-show" ? "no-show" : mode;
      const body: any = { if_version: event.version };
      if (["cancel", "no-show", "reschedule", "reject"].includes(mode)) body.reason = reason.trim();
      if (mode === "complete") {
        body.outcome = outcome.trim();
        body.evidence_note = evidenceNote.trim() || null;
        body.evidence_links = evidenceLinks.split(/\r?\n/).map((v) => v.trim()).filter(Boolean);
      }
      await api.post(`/crm/events/${event.id}/${path}`, body, {
        headers: { "Idempotency-Key": keyRef.current },
      });
      keyRef.current = makeKey();
      setDialog(null); setReason(""); setOutcome(""); setEvidenceNote(""); setEvidenceLinks("");
      onRefetch();
    } catch (e: any) {
      setError(formatApiError(e) || "Action failed");
    } finally {
      setSubmitting(false);
    }
  }

  const timeLabel = event.all_day
    ? (event.start_date === event.end_date
        ? event.start_date
        : `${event.start_date} → ${event.end_date}`)
    : event.start_at
      ? `${new Date(event.start_at).toLocaleString()} – ${event.end_at ? new Date(event.end_at).toLocaleTimeString() : ""}`
      : "";

  return (
    <div className="border border-slate-200 rounded-md bg-white p-3 flex items-start justify-between gap-3"
         data-testid={`event-item-${event.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 truncate" data-testid="event-item-title">{event.title}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.cls}`}
                data-testid={`event-item-badge-${badge.text.toLowerCase().replace(/ /g,"-")}`}>{badge.text}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {event.event_type_code} · {event.all_day ? "all day" : timeLabel}
          {event.location ? ` · ${event.location}` : ""}
        </div>
        {event.cancelled_reason ? (
          <div className="text-xs text-slate-500 mt-1">Cancelled: {event.cancelled_reason}</div>
        ) : null}
        {event.completion_outcome ? <div className="text-xs text-slate-600 mt-1">Outcome: {event.completion_outcome}</div> : null}
        {event.verification_rejection_reason ? <div className="text-xs text-orange-700 mt-1">Rejected: {event.verification_rejection_reason}</div> : null}
        <div className="mt-1 space-y-0.5 text-xs text-slate-500" data-testid={`event-attribution-${event.id}`}>
          <div>Created by {event.created_by_name || "Unknown user"}{event.created_at ? ` · ${new Date(event.created_at).toLocaleString()}` : ""}</div>
          <div>Assigned to {event.assigned_to_name || "Unknown user"}</div>
          {event.submitted_for_verification_at && <div>Submitted by {event.completed_by_name || "Unknown user"} · {new Date(event.submitted_for_verification_at).toLocaleString()}</div>}
          {event.verified_at && <div>Verified by {event.verified_by_name || "Unknown user"} · {new Date(event.verified_at).toLocaleString()}</div>}
          {event.cancelled_at && <div>Cancelled by {event.cancelled_by_name || "Unknown user"} · {new Date(event.cancelled_at).toLocaleString()}</div>}
          {event.no_show_at && <div>No-show recorded by {event.no_show_by_name || "Unknown user"} · {new Date(event.no_show_at).toLocaleString()}</div>}
          {event.rescheduled_at && <div>Rescheduled by {event.rescheduled_by_name || "Unknown user"} · {new Date(event.rescheduled_at).toLocaleString()}</div>}
          {event.updated_at && event.updated_at !== event.created_at && <div>Last updated by {event.updated_by_name || "Unknown user"} · {new Date(event.updated_at).toLocaleString()}</div>}
        </div>
      </div>
      {!readOnly && event.status === "scheduled" ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button size="sm" onClick={() => setDialog("complete")}
            disabled={submitting}
            data-testid={`event-item-complete-btn-${event.id}`}>Complete</Button>
          <Button size="sm" variant="outline" onClick={() => setDialog("no-show")}
            disabled={submitting}
            data-testid={`event-item-noshow-btn-${event.id}`}>No-show</Button>
          <Button size="sm" variant="ghost" onClick={() => setDialog("cancel")}
            disabled={submitting}
            data-testid={`event-item-cancel-btn-${event.id}`}>Cancel</Button>
          <Button size="sm" variant="ghost" onClick={() => setDialog("reschedule")}
            disabled={submitting}>Reschedule</Button>
        </div>
      ) : null}
      {!readOnly && event.status === "rejected" ? (
        <Button size="sm" onClick={() => setDialog("complete")}>Resubmit</Button>
      ) : null}
      {!readOnly && canVerify && event.status === "pending_verification" ? (
        <div className="flex gap-1">
          <Button size="sm" onClick={() => runAction("verify")}>Verify</Button>
          <Button size="sm" variant="outline" onClick={() => setDialog("reject")}>Reject</Button>
        </div>
      ) : null}

      <Dialog open={dialog === "complete"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="event-complete-dialog">
          <DialogHeader>
            <DialogTitle>Submit visit for verification</DialogTitle>
            <DialogDescription>Record what happened. Only verified visits count as completed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor={`event-outcome-${event.id}`}>Outcome *</Label>
              <Input id={`event-outcome-${event.id}`} value={outcome} onChange={(e) => setOutcome(e.target.value)}
                placeholder="e.g. Met physician; follow-up requested" data-testid="event-completion-outcome" />
            </div>
            <div><Label htmlFor={`event-evidence-note-${event.id}`}>Evidence or notes (optional)</Label>
              <Textarea id={`event-evidence-note-${event.id}`} value={evidenceNote}
                onChange={(e) => setEvidenceNote(e.target.value)} rows={3} />
            </div>
            <div><Label htmlFor={`event-evidence-links-${event.id}`}>File links (optional, one per line)</Label>
              <Textarea id={`event-evidence-links-${event.id}`} value={evidenceLinks}
                onChange={(e) => setEvidenceLinks(e.target.value)} rows={2} />
            </div>
          </div>
          {error ? <div role="alert" className="text-sm text-red-600" data-testid="event-complete-error">{error}</div> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}
              data-testid="event-complete-cancel-btn">Cancel</Button>
            <Button onClick={() => runAction("complete")} disabled={submitting || !outcome.trim()}
              data-testid="event-complete-submit-btn">Submit for verification</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "reject" || dialog === "reschedule"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog === "reject" ? "Reject completion evidence" : "Reschedule visit"}</DialogTitle>
            <DialogDescription>A reason is required for the audit history.</DialogDescription>
          </DialogHeader>
          <Label htmlFor={`event-reason-${event.id}`}>Reason</Label>
          <Textarea id={`event-reason-${event.id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          {error ? <div role="alert" className="text-sm text-red-600">{error}</div> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Close</Button>
            <Button onClick={() => dialog && runAction(dialog)} disabled={submitting || !reason.trim()}>
              {dialog === "reject" ? "Reject" : "Record reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "cancel"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="event-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel appointment</DialogTitle>
            <DialogDescription>Provide a reason (required).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`event-cancel-reason-${event.id}`}>Reason</Label>
            <Textarea id={`event-cancel-reason-${event.id}`} value={reason}
              onChange={(e) => setReason(e.target.value)} rows={3}
              data-testid="event-cancel-reason-input" />
            {error ? <div role="alert" className="text-sm text-red-600" data-testid="event-cancel-error">{error}</div> : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}
              data-testid="event-cancel-close-btn">Close</Button>
            <Button onClick={() => runAction("cancel")}
              disabled={submitting || reason.trim().length === 0}
              data-testid="event-cancel-submit-btn">Cancel appointment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "no-show"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="event-noshow-dialog">
          <DialogHeader>
            <DialogTitle>Mark as no-show</DialogTitle>
            <DialogDescription>Optional note.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`event-noshow-reason-${event.id}`}>Note</Label>
            <Textarea id={`event-noshow-reason-${event.id}`} value={reason}
              onChange={(e) => setReason(e.target.value)} rows={2}
              data-testid="event-noshow-reason-input" />
            {error ? <div role="alert" className="text-sm text-red-600" data-testid="event-noshow-error">{error}</div> : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}
              data-testid="event-noshow-close-btn">Close</Button>
            <Button onClick={() => runAction("no-show")} disabled={submitting}
              data-testid="event-noshow-submit-btn">Mark as no-show</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const EventList: React.FC<{
  events: EventShape[]; onRefetch: () => void; readOnly?: boolean;
  emptyText?: string; testId?: string;
}> = ({ events, onRefetch, readOnly, emptyText, testId }) => {
  if (!events || events.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-3"
        data-testid={testId ? `${testId}-empty` : "event-list-empty"}>
        {emptyText || "No appointments."}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={testId || "event-list"}>
      {events.map((e) => (
        <EventItem key={e.id} event={e} onRefetch={onRefetch} readOnly={readOnly} />
      ))}
    </div>
  );
};
